// DoctorProfileSeo.jsx
import React from "react";

/**
 * DoctorProfileSeo
 * - Public profile SEO (indexable)
 * - Uses schema.org Physician + AggregateRating + OpeningHoursSpecification
 * - Supports meta keywords (string | string[])
 */
const DoctorProfileSeo = ({
  title,
  description,
  canonicalUrl,
  imageUrl,
  robots = "index,follow",
  doctorJsonLd = {},
  breadcrumbs = [],
  /** NEW: keywords can be a string "a,b,c" or an array ["a","b","c"] */
  keywords,
}) => {
  const breadLd = breadcrumbs.length
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.item,
        })),
      }
    : null;

  // --- NEW: normalize keywords to a single comma-separated string
  const normalizeKeywords = (kw) => {
    if (!kw) return "";
    const list = Array.isArray(kw)
      ? kw
      : String(kw)
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

    // de-dup (case-insensitive), keep readable order, cap ~60 terms / ~1000 chars
    const seen = new Set();
    const deduped = [];
    for (const k of list) {
      const key = k.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(k);
      }
      if (deduped.length >= 60) break;
    }
    let joined = deduped.join(", ");
    if (joined.length > 1000) {
      joined = joined.slice(0, 1000).replace(/,\s*[^,]*$/, ""); // trim to last full token
    }
    return joined;
  };

  const keywordContent = normalizeKeywords(keywords);

  return (
    <>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      <meta name="robots" content={robots} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* NEW: Keywords */}
      {keywordContent && <meta name="keywords" content={keywordContent} />}

      {/* Open Graph */}
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={title} />
      {description && <meta property="og:description" content={description} />}
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      {description && <meta name="twitter:description" content={description} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* Main JSON-LD for the physician */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(doctorJsonLd) }}
      />

      {/* Breadcrumbs */}
      {breadLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadLd) }}
        />
      )}
    </>
  );
};

export default DoctorProfileSeo;
